import { Download, Upload, Users } from "lucide-react";
import type { StakeholderRelation } from "../../../hooks";
import {
  groupStakeholderRelations,
  type RelationViewAxis,
} from "../groupStakeholderRelations";

interface StakeholderRelationSectionProps {
  stakeholderRelations: readonly StakeholderRelation[];
  loading: boolean;
  hasSettings: boolean;
  onImportClick: () => void;
  onDelete: (relation: StakeholderRelation) => void;
  viewAxis: RelationViewAxis;
  onViewAxisChange: (axis: RelationViewAxis) => void;
  onExportCsv: () => void;
}

export function StakeholderRelationSection({
  stakeholderRelations,
  loading,
  hasSettings,
  onImportClick,
  onDelete,
  viewAxis,
  onViewAxisChange,
  onExportCsv,
}: StakeholderRelationSectionProps) {
  const groups = groupStakeholderRelations(stakeholderRelations, viewAxis);
  const axisLabel = viewAxis === "respondent" ? "回答者" : "受け取る人";
  const counterpartLabel = viewAxis === "respondent" ? "受け取る人" : "回答者";

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
      <div className="flex flex-wrap items-center justify-between gap-4 border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
        <div>
          <h2 className="m-0 font-bold text-2xl text-[var(--color-primary-500)]">
            関係性
          </h2>
          <p className="mt-1 mb-0 text-[13px] text-[var(--color-text-secondary)]">
            誰が誰について語るかの組み合わせ。回答者ごとに1回のインタビューを実施します
          </p>
          {!hasSettings && (
            <p className="mt-1 mb-0 text-[#b45309] text-[13px]">
              入稿するには、先にインタビュー設定を追加してください
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-[var(--color-border-default)]">
            {(
              [
                ["respondent", "回答者から見る"],
                ["recipient", "受け取る人から見る"],
              ] as const
            ).map(([axis, label]) => (
              <button
                key={axis}
                type="button"
                onClick={() => onViewAxisChange(axis)}
                aria-pressed={viewAxis === axis}
                className={`whitespace-nowrap px-3 py-2 font-medium text-[13px] ${
                  viewAxis === axis
                    ? "bg-[var(--color-primary-500)] text-white"
                    : "cursor-pointer bg-white text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle-gray)]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onExportCsv}
            disabled={stakeholderRelations.length === 0}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-md border px-4 py-2 font-medium text-sm ${
              stakeholderRelations.length === 0
                ? "cursor-not-allowed border-[var(--color-border-default)] text-[#9ca3af]"
                : "cursor-pointer border-[var(--color-primary-500)] text-[var(--color-primary-500)] hover:bg-[var(--color-bg-subtle-gray)]"
            }`}
          >
            <Download className="h-4 w-4" />
            CSV書き出し
          </button>
          <button
            type="button"
            onClick={onImportClick}
            disabled={!hasSettings}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-md border-none px-4 py-2 font-medium text-sm text-white ${
              hasSettings
                ? "cursor-pointer bg-[var(--color-primary-500)] hover:bg-[#045a94]"
                : "cursor-not-allowed bg-[#9ca3af]"
            }`}
          >
            <Upload className="h-4 w-4" />
            一括入稿
          </button>
        </div>
      </div>
      {loading ? (
        <div className="p-12 text-center font-medium text-[var(--color-text-secondary)]">
          読み込み中...
        </div>
      ) : stakeholderRelations.length === 0 ? (
        <div className="p-12 text-center">
          <Users className="mx-auto mb-4 h-12 w-12 text-[#9ca3af]" />
          <p className="text-[var(--color-text-secondary)]">
            関係性がまだ登録されていません
          </p>
        </div>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[var(--color-bg-subtle-gray)]">
              {[
                axisLabel,
                counterpartLabel,
                "インタビュー設定",
                "回答",
                "操作",
              ].map((label) => (
                <th
                  key={label}
                  className="whitespace-nowrap border-[var(--color-border-default)] border-b px-6 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]"
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((group) =>
              group.relations.map((relation, indexInGroup) => (
                <tr
                  key={relation.stakeholderRelationId}
                  className="border-[var(--color-border-default)] border-b"
                >
                  {indexInGroup === 0 && (
                    <td
                      rowSpan={group.relations.length}
                      className="border-[var(--color-border-default)] border-r px-6 py-4 align-top text-sm"
                    >
                      <div className="font-medium text-[var(--color-text-primary)]">
                        {group.name}
                      </div>
                      <div className="text-[13px] text-[var(--color-text-secondary)]">
                        {group.email}
                      </div>
                      {group.relations.length > 1 && (
                        <div className="mt-1 text-[12px] text-[var(--color-text-secondary)]">
                          {group.relations.length}件
                        </div>
                      )}
                    </td>
                  )}
                  <td className="px-6 py-4 text-sm">
                    <div className="font-medium text-[var(--color-text-primary)]">
                      {viewAxis === "respondent"
                        ? relation.recipientName
                        : relation.respondentName}
                    </div>
                    <div className="text-[13px] text-[var(--color-text-secondary)]">
                      {viewAxis === "respondent"
                        ? relation.recipientEmail
                        : relation.respondentEmail}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-[var(--color-text-secondary)] text-sm">
                    {relation.stakeholderSettingName}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span
                      className={`rounded-full px-2.5 py-1 font-medium text-[12px] ${
                        relation.hasAnswer
                          ? "bg-[#dcfce7] text-[#166534]"
                          : "bg-[var(--color-bg-subtle-gray)] text-[var(--color-text-secondary)]"
                      }`}
                    >
                      {relation.hasAnswer ? "回答済み" : "未回答"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {relation.hasAnswer ? (
                      <span className="text-[#9ca3af] text-[13px]">
                        回答済みのため削除できません
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onDelete(relation)}
                        className="cursor-pointer border-none bg-none p-0 text-[#ef4444] hover:underline"
                      >
                        削除
                      </button>
                    )}
                  </td>
                </tr>
              )),
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
